import axios from 'axios';

// Base URL for Mock Parser API
const API_BASE = '/api/mock-parser';

// Create axios instance with default config
const mockParserClient = axios.create({
  baseURL: API_BASE,
  timeout: 120000, // 2 minutes for AI processing
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
mockParserClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
mockParserClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message = error.response?.data?.message || error.message || 'An error occurred';
    return Promise.reject(new Error(message));
  }
);

/**
 * Mock Parser API Service
 */
export const mockParserApi = {
  /**
   * Upload and parse a PYQ PDF
   * @param {File} file - PDF file
   * @param {Object} metadata - { sourceExam, sourceYear, paperType, subject }
   * @returns {Promise<Object>} Parse result
   */
  uploadPYQ: async (file, metadata) => {
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('paperType', metadata.paperType);
    formData.append('subject', metadata.subject);
    formData.append('sourceYear', metadata.sourceYear.toString());
    if (metadata.sourceSession) {
      formData.append('sourceSession', metadata.sourceSession);
    }

    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'multipart/form-data',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await axios.post(`${API_BASE}/pyq/upload`, formData, {
      headers,
      timeout: 120000,
    });

    return response.data;
  },

  /**
   * Get question bank statistics
   * @returns {Promise<Object>} Statistics
   */
  getStats: async () => {
    return mockParserClient.get('/questions/stats');
  },

  /**
   * Get PYQ statistics (uploaded question papers)
   * @returns {Promise<Object>} PYQ Statistics
   */
  getPYQStats: async () => {
    return mockParserClient.get('/pyq/stats');
  },

  /**
   * Get AI-generated Question Bank statistics
   * @returns {Promise<Object>} Question Bank Statistics
   */
  getQuestionBankStats: async () => {
    return mockParserClient.get('/v2/question-bank/stats');
  },

  /**
   * Delete PYQs by filters
   * @param {Object} filters - { paperType, subject, sourceYear }
   * @returns {Promise<Object>} Delete result
   */
  deletePYQs: async (filters) => {
    return mockParserClient.delete('/pyq', { params: filters });
  },

  /**
   * Get questions with optional filters
   * @param {Object} filters - { exam, paper, year, subject, limit }
   * @returns {Promise<Object>} Questions list
   */
  getQuestions: async (filters = {}) => {
    return mockParserClient.get('/v2/question-bank', { params: filters });
  },

  /**
   * Get random questions
   * @param {Object} params - { exam, paper, count }
   * @returns {Promise<Object>} Random questions
   */
  getRandomQuestions: async (params) => {
    return mockParserClient.get('/questions/random', { params });
  },

  /**
   * Search questions by text
   * @param {string} query - Search query
   * @param {number} limit - Max results
   * @returns {Promise<Object>} Search results
   */
  searchQuestions: async (query, limit = 20) => {
    return mockParserClient.get('/questions/search', {
      params: { q: query, limit },
    });
  },

  /**
   * Find similar questions using vector search
   * @param {string} queryText - Query text
   * @param {Object} filters - { exam, limit }
   * @returns {Promise<Object>} Similar questions
   */
  findSimilarQuestions: async (queryText, filters = {}) => {
    return mockParserClient.post('/questions/similar', {
      queryText,
      ...filters,
    });
  },

  /**
   * Generate a new mock test
   * @param {Object} config - { mode, exam, paper, questionCount, duration }
   * @returns {Promise<Object>} Generated mock test info
   */
  generateMockTest: async (config) => {
    return mockParserClient.post('/mock-test/generate', config);
  },

  /**
   * Get mock test by ID
   * @param {string} testId - Test ID
   * @returns {Promise<Object>} Mock test with questions
   */
  getMockTest: async (testId) => {
    return mockParserClient.get(`/mock-test/${testId}`);
  },

  /**
   * Get answer sheet for a mock test
   * @param {string} testId - Test ID
   * @returns {Promise<Object>} Answer sheet with reasoning
   */
  getAnswerSheet: async (testId) => {
    return mockParserClient.get(`/mock-test/answer-sheet/${testId}`);
  },

  /**
   * Get recent mock tests
   * @param {number} limit - Number of tests
   * @returns {Promise<Object>} Recent tests
   */
  getRecentTests: async (limit = 10) => {
    return mockParserClient.get('/mock-tests', { params: { limit } });
  },

  /**
   * Delete a question by ID
   * @param {string} questionId - Question ID
   * @returns {Promise<Object>} Delete result
   */
  deleteQuestion: async (questionId) => {
    return mockParserClient.delete(`/questions/${questionId}`);
  },

  /**
   * Delete a mock test by ID
   * @param {string} testId - Test ID
   * @returns {Promise<Object>} Delete result
   */
  deleteMockTest: async (testId) => {
    return mockParserClient.delete(`/mock-test/${testId}`);
  },
};

export default mockParserApi;
